import { authenticatedInstance } from '../index/index.service';
import type { OrderlineView } from 'shared-models/browser';
import { isMinOrderCompletionNote, MIN_ORDER_COMPLETION_LABEL } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import { logToBackend } from '../client-log/client-log.service';

export type { OrderlineStatus, OrderlineView } from 'shared-models/browser';

export const ORDERLINE_GONE = 'ORDERLINE_GONE';
export const GENERAL_ITEM_LABEL = 'פריט כללי';

// Display label for any orderline; "פריט כללי" when it has no product. See md/GENERAL_ITEM.md.
export const orderlineDisplayName = (line: { productName: string | null; note?: string | null }): string =>
  line.productName ?? (isMinOrderCompletionNote(line.note) ? MIN_ORDER_COMPLETION_LABEL : GENERAL_ITEM_LABEL);

class OrderlinesService {
  // Summary: Lists orderlines for a tenant order so the POS order screen can render the cart.
  // Details: docs/function-library/web/services.md#orderlines-service
  async listForOrder(
    tenantId: string,
    orderId: number,
    employeeSession?: string | null,
    options: { includeCanceled?: boolean } = {},
  ): Promise<OrderlineView[]> {
    try {
      const response = await authenticatedInstance.get<OrderlineView[]>(
        `/orderlines/tenant/${tenantId}/order/${orderId}`,
        {
          ...(options.includeCanceled ? { params: { includeCanceled: 'true' } } : {}),
          ...(employeeSession ? { headers: { 'x-employee-session': employeeSession } } : {}),
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading orderlines'));
    }
  }

  // Summary: Adds a single orderline to an open order from a product tap.
  // Details: docs/function-library/web/services.md#orderlines-service
  async add(
    tenantId: string,
    orderId: number,
    productId: number,
    quantity: number,
    employeeSession: string,
    selections: Array<{ itemId: number; selectedQuantity: number; selectionSort: number }> = [],
  ): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/orderlines/tenant/${tenantId}/order/${orderId}`,
        selections.length > 0 ? { productId, quantity, selections } : { productId, quantity },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed adding orderline'));
    }
  }

  // Summary: Adds a general item ("פריט כללי") to an open order; returns the created line. Note attached
  // separately via updateNote. Details: docs/function-library/web/services.md#orderlines-service
  async addGeneralItem(
    tenantId: string,
    orderId: number,
    unitPrice: number,
    quantity: number,
    employeeSession: string,
    refundCode?: string,
  ): Promise<OrderlineView> {
    try {
      const response = await authenticatedInstance.post<OrderlineView>(
        `/orderlines/tenant/${tenantId}/order/${orderId}/general-item`,
        refundCode ? { unitPrice, quantity, refundCode } : { unitPrice, quantity },
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      const thrown: Error & {
        ownerEmail?: string;
        deliveryChannel?: 'email' | 'push';
        deliveryTargetLabel?: string;
      } = new Error(
        extractServiceErrorMessage(error, 'Failed adding general item'),
      );
      const ownerEmail = (error as { response?: { data?: { ownerEmail?: unknown } } }).response?.data?.ownerEmail;
      if (typeof ownerEmail === 'string') {
        thrown.ownerEmail = ownerEmail;
      }
      const deliveryChannel = (error as { response?: { data?: { deliveryChannel?: unknown } } }).response?.data?.deliveryChannel;
      if (deliveryChannel === 'email' || deliveryChannel === 'push') {
        thrown.deliveryChannel = deliveryChannel;
      }
      const deliveryTargetLabel = (error as { response?: { data?: { deliveryTargetLabel?: unknown } } }).response?.data?.deliveryTargetLabel;
      if (typeof deliveryTargetLabel === 'string' && deliveryTargetLabel.trim().length > 0) {
        thrown.deliveryTargetLabel = deliveryTargetLabel;
      }
      throw thrown;
    }
  }

  async upsertMinOrderCompletion(
    tenantId: string,
    orderId: number,
    amount: number,
    note: string,
    employeeSession: string,
  ): Promise<OrderlineView | null> {
    try {
      const response = await authenticatedInstance.put<OrderlineView | null>(
        `/orderlines/tenant/${tenantId}/order/${orderId}/min-order-completion`,
        { amount, note },
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data ?? null;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating min-order completion'));
    }
  }

  // Summary: Sets the absolute quantity of a single pending orderline (the cart +/- adjust path). 204; canonical change rides the orderline.updated SSE.
  // Details: docs/function-library/web/services.md#orderlines-service
  async updateQuantity(
    tenantId: string,
    orderId: number,
    orderlineId: number,
    quantity: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orderlines/tenant/${tenantId}/order/${orderId}/line/${orderlineId}`,
        { quantity },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating orderline quantity'));
    }
  }

  // Summary: Adjusts a pending line's qty by a signed delta; backend clamps [1,99]. 204.
  async adjustQuantity(
    tenantId: string,
    orderId: number,
    orderlineId: number,
    quantityDelta: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orderlines/tenant/${tenantId}/order/${orderId}/line/${orderlineId}`,
        { quantityDelta },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating orderline quantity'));
    }
  }

  // Summary: Replaces a pending line's variant/combo selections (edit-in-place). 204; the re-priced line rides the orderline.updated SSE. Prices resolved server-side.
  // Details: docs/function-library/web/services.md#orderlines-service
  async updateSelections(
    tenantId: string,
    orderId: number,
    orderlineId: number,
    selections: Array<{ itemId: number; selectedQuantity: number; selectionSort: number }>,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orderlines/tenant/${tenantId}/order/${orderId}/line/${orderlineId}/selections`,
        { selections },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        throw new Error(ORDERLINE_GONE);
      }
      throw new Error(extractServiceErrorMessage(error, 'Failed updating orderline selections'));
    }
  }

  // Summary: Removes a single pending orderline (the cart remove / decrement-to-zero path). 204; the orderline.removed SSE drops it everywhere.
  // Details: docs/function-library/web/services.md#orderlines-service
  async remove(
    tenantId: string,
    orderId: number,
    orderlineId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.delete<void>(
        `/orderlines/tenant/${tenantId}/order/${orderId}/line/${orderlineId}`,
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed removing orderline'));
    }
  }

  // Summary: Sets/clears a line's free-text kitchen note. 204; the updated line (with note) rides the orderline.updated SSE.
  // Details: docs/function-library/web/services.md#orderlines-service
  async updateNote(
    tenantId: string,
    orderId: number,
    orderlineId: number,
    note: string | null,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/orderlines/tenant/${tenantId}/order/${orderId}/line/${orderlineId}/note`,
        { note: note && note.trim().length > 0 ? note.trim() : null },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating orderline note'));
    }
  }

  // Summary: Sends all currently-pending orderlines on an open order to the kitchen and print pipeline.
  // Details: docs/function-library/web/services.md#orderlines-service
  async sendToKitchen(
    tenantId: string,
    orderId: number,
    employeeSession: string,
    options?: {
      skipPrintJobs?: boolean;
      markSkipped?: boolean;
      actionGroupId?: string;
      selectedOrderlineIds?: number[];
      selectedOrderlineQuantities?: Array<{ orderlineId: number; quantity: number }>;
      expectedPrinterRoutes?: Array<{ printerId: number; printerName: string; orderlineIds: number[] }>;
    },
  ): Promise<void> {
    const body: {
      skipPrintJobs?: boolean;
      markSkipped?: boolean;
      actionGroupId?: string;
      selectedOrderlineIds?: number[];
      selectedOrderlineQuantities?: Array<{ orderlineId: number; quantity: number }>;
      expectedPrinterRoutes?: Array<{ printerId: number; printerName: string; orderlineIds: number[] }>;
    } = {};
    if (options?.skipPrintJobs === true) {
      body.skipPrintJobs = true;
    }
    if (options?.markSkipped === true) {
      body.markSkipped = true;
    }
    if (options?.actionGroupId) {
      body.actionGroupId = options.actionGroupId;
    }
    if (Array.isArray(options?.selectedOrderlineIds) && options.selectedOrderlineIds.length > 0) {
      body.selectedOrderlineIds = options.selectedOrderlineIds;
    }
    if (Array.isArray(options?.selectedOrderlineQuantities) && options.selectedOrderlineQuantities.length > 0) {
      body.selectedOrderlineQuantities = options.selectedOrderlineQuantities;
    }
    if (Array.isArray(options?.expectedPrinterRoutes) && options.expectedPrinterRoutes.length > 0) {
      body.expectedPrinterRoutes = options.expectedPrinterRoutes;
    }
    const startedAt = Date.now();
    logToBackend('green-fire-trace', 'send-to-kitchen HTTP request starting', {
      trace: options?.actionGroupId,
      orderId,
      expectedPrinterRoutes: options?.expectedPrinterRoutes,
      selectedOrderlineIds: options?.selectedOrderlineIds,
    }, 'info');
    try {
      await authenticatedInstance.post<void>(
        `/orderlines/tenant/${tenantId}/order/${orderId}/send-to-kitchen`,
        body,
        { headers: { 'x-employee-session': employeeSession } },
      );
      logToBackend('green-fire-trace', 'send-to-kitchen HTTP request succeeded', {
        trace: options?.actionGroupId,
        orderId,
        elapsedMs: Date.now() - startedAt,
      }, 'info');
    } catch (error: unknown) {
      logToBackend('green-fire-trace', 'send-to-kitchen HTTP request failed', {
        trace: options?.actionGroupId,
        orderId,
        reason: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      }, 'error');
      throw new Error(extractServiceErrorMessage(error, 'Failed sending orderlines to kitchen'));
    }
  }

  // Summary: Manager-only reprint for already-sent orderlines; selected quantities are logged/printed as kind=reprint.
  async reprint(
    tenantId: string,
    orderId: number,
    employeeSession: string,
    selections: Array<{ orderlineId: number; quantity: number }>,
    actionGroupId?: string,
    skipPrintJobs?: boolean,
  ): Promise<{ printerGroups: Array<{ printerId: number; printerName: string; lines: Array<{ orderlineId: number; productName: string; quantity: number; kdsVisible: boolean }> }> }> {
    try {
      const body: Record<string, unknown> = { selections };
      if (actionGroupId) body.actionGroupId = actionGroupId;
      if (skipPrintJobs) body.skipPrintJobs = true;
      const response = await authenticatedInstance.post(
        `/orderlines/tenant/${tenantId}/order/${orderId}/reprint`,
        body,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return { printerGroups: response.data?.printerGroups ?? [] };
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed reprinting orderlines'));
    }
  }
}

export const orderlinesService = new OrderlinesService();
